import jsPDF from 'jspdf';

export interface RecommendationReport {
  id: string;
  title: string;
  content: string;
  key_findings: string[];
  recommendations: {
    action: string;
    ticker: string;
    reasoning: string;
    confidence: 'high' | 'medium' | 'low';
    impact?: 'high' | 'medium' | 'low';
  }[];
  generated_at: string;
  analyst_notes?: string;
  risk_alerts?: Array<{
    title: string;
    message: string;
    level: 'low' | 'medium' | 'high';
    confidence: number;
    urgency?: 'immediate' | 'monitor';
    data_points?: Array<{
      value: string;
      metric: string;
      concern: string;
      threshold: string;
    }>;
    affected_positions?: string[];
    extended_reasoning?: string;
    recommended_action?: string;
  }>;
}

// Helper function to clean special characters that jsPDF can't handle
const cleanText = (text: string): string => {
  return (
    text
      // Convert common emojis to ASCII text (jsPDF can't render Unicode symbols)
      .replace(/✅/g, '*')
      .replace(/✓/g, '*')
      .replace(/✔/g, '*')
      .replace(/❌/g, 'X')
      .replace(/✗/g, 'X')
      .replace(/✘/g, 'X')
      .replace(/⚠️/g, '[!]')
      .replace(/⚠/g, '[!]')
      .replace(/ℹ️/g, '[i]')
      .replace(/ℹ/g, '[i]')
      // Arrow symbols
      .replace(/→/g, '->')
      .replace(/←/g, '<-')
      .replace(/↑/g, '^')
      .replace(/↓/g, 'v')
      .replace(/➝/g, '->')
      .replace(/➔/g, '->')
      .replace(/➜/g, '->')
      .replace(/⬆/g, '^')
      .replace(/⬇/g, 'v')
      // Quote marks
      .replace(/'/g, "'")
      .replace(/'/g, "'")
      .replace(/"/g, '"')
      .replace(/"/g, '"')
      // Dashes
      .replace(/–/g, '-')
      .replace(/—/g, '-')
      .replace(/…/g, '...')
      // Bullet points that show up as weird characters
      .replace(/•/g, '- ')
      .replace(/◦/g, '- ')
      .replace(/▪/g, '- ')
      .replace(/▫/g, '- ')
      // Math symbols
      .replace(/≤/g, '<=')
      .replace(/≥/g, '>=')
      .replace(/≠/g, '!=')
      .replace(/≈/g, '~=')
      // Problematic characters
      .replace(/þ/g, '- ')
      .replace(/§/g, '')
      .replace(/¶/g, '')
      // General emoji removal as fallback (removes most remaining emojis)
      .replace(/[\u{1F300}-\u{1F9FF}]/gu, '[emoji]')
      .replace(/[\u{2600}-\u{26FF}]/gu, '')
      .replace(/[\u{2700}-\u{27BF}]/gu, '')
  );
};

export const generateReportPDF = async (
  report: RecommendationReport,
  portfolioName?: string,
) => {
  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Page dimensions and margins
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;
    let yPosition = margin;

    // Helper function to add new page if needed
    const checkPageBreak = (requiredSpace: number) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Add Banqora header with white background
    pdf.setFillColor(255, 255, 255); // White background
    pdf.rect(margin, yPosition, contentWidth, 15, 'F');

    // Load and add logo
    let logoAdded = false;
    try {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      // Use relative path that works in production (public folder)
      logoImg.src = '/icon-512.png';

      await new Promise<void>((resolve, _reject) => {
        logoImg.onload = () => {
          try {
            // Add logo to PDF (small size in header)
            const logoSize = 12; // 12mm square
            pdf.addImage(
              logoImg,
              'PNG',
              margin + 2,
              yPosition + 1.5,
              logoSize,
              logoSize,
            );
            logoAdded = true;
            resolve();
          } catch (error) {
            console.warn('Error adding logo to PDF:', error);
            resolve();
          }
        };
        logoImg.onerror = () => {
          console.warn('Failed to load logo');
          resolve();
        };
        // Timeout after 3 seconds
        setTimeout(() => resolve(), 3000);
      });
    } catch (error) {
      console.warn('Error loading logo:', error);
    }

    // Add blue text
    pdf.setTextColor(37, 99, 235); // Banqora blue text
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    const titleX = logoAdded ? margin + 17 : margin + 5;
    pdf.text('Banqora: Portfolio Report', titleX, yPosition + 10);
    yPosition += 22; // Increased from 20 to 22 (added 2mm padding)

    // Portfolio name and date
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    const dateStr = new Date(report.generated_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    if (portfolioName) {
      pdf.text(`Portfolio: ${portfolioName}`, margin, yPosition);
      yPosition += 7;
    }
    pdf.text(`Generated: ${dateStr}`, margin, yPosition);
    yPosition += 12;

    // Report title
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    const titleLines = pdf.splitTextToSize(
      cleanText(report.title),
      contentWidth,
    );
    titleLines.forEach((line: string) => {
      checkPageBreak(8);
      pdf.text(line, margin, yPosition);
      yPosition += 8;
    });
    yPosition += 5;

    // Process markdown content - clean special characters first
    const cleanedContent = cleanText(report.content);
    const lines = cleanedContent.split('\n');

    // Helper function to detect if a line is a list item
    const isListItem = (line: string): boolean => {
      const trimmed = line.trim();
      return (
        trimmed.startsWith('-') ||
        trimmed.startsWith('•') ||
        trimmed.startsWith('*') ||
        /^\d+\./.test(trimmed) // numbered list
      );
    };

    // Helper function to detect if a line is a table row
    const isTableRow = (line: string): boolean => {
      const trimmed = line.trim();
      return trimmed.startsWith('|') && trimmed.endsWith('|');
    };

    // Helper function to detect table separator row (|---|---|)
    const isTableSeparator = (line: string): boolean => {
      const trimmed = line.trim();
      return isTableRow(line) && /^[\|\s\-:]+$/.test(trimmed);
    };

    // Helper function to parse table cells from a row
    const parseTableRow = (line: string): string[] => {
      const trimmed = line.trim();
      // Remove leading and trailing pipes, then split by |
      const content = trimmed.slice(1, -1);
      return content.split('|').map((cell) => cell.trim());
    };

    // Helper function to render a markdown table
    const renderTable = (tableLines: string[]) => {
      if (tableLines.length < 2) return;

      // Parse header and data rows (skip separator)
      const headerCells = parseTableRow(tableLines[0]);
      const dataRows: string[][] = [];

      for (let i = 1; i < tableLines.length; i++) {
        if (!isTableSeparator(tableLines[i])) {
          dataRows.push(parseTableRow(tableLines[i]));
        }
      }

      const numCols = headerCells.length;
      if (numCols === 0) return;

      // Calculate column widths based on content
      const colWidths: number[] = [];
      pdf.setFontSize(9);

      for (let col = 0; col < numCols; col++) {
        let maxWidth = pdf.getTextWidth(headerCells[col] || '');
        for (const row of dataRows) {
          const cellWidth = pdf.getTextWidth(row[col] || '');
          if (cellWidth > maxWidth) maxWidth = cellWidth;
        }
        // Add padding
        colWidths.push(maxWidth + 6);
      }

      // Scale columns to fit page width if needed
      const totalWidth = colWidths.reduce((a, b) => a + b, 0);
      if (totalWidth > contentWidth) {
        const scale = contentWidth / totalWidth;
        for (let i = 0; i < colWidths.length; i++) {
          colWidths[i] *= scale;
        }
      }

      const rowHeight = 7;
      const tableWidth = colWidths.reduce((a, b) => a + b, 0);

      // Check if table fits on current page (header + at least 2 data rows)
      const minTableHeight = rowHeight * Math.min(3, dataRows.length + 1);
      checkPageBreak(minTableHeight + 5);

      // Draw header row with background
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPosition - 4, tableWidth, rowHeight, 'F');

      // Draw header text
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      let xPos = margin;
      for (let col = 0; col < numCols; col++) {
        const cellText = headerCells[col] || '';
        pdf.text(cellText, xPos + 2, yPosition);
        xPos += colWidths[col];
      }
      yPosition += rowHeight;

      // Draw data rows
      pdf.setFont('helvetica', 'normal');
      for (const row of dataRows) {
        checkPageBreak(rowHeight);

        // Alternate row background
        xPos = margin;
        for (let col = 0; col < numCols; col++) {
          const cellText = row[col] || '';
          pdf.text(cellText, xPos + 2, yPosition);
          xPos += colWidths[col];
        }

        // Draw row border
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, yPosition + 2, margin + tableWidth, yPosition + 2);
        yPosition += rowHeight;
      }

      // Draw table borders
      pdf.setDrawColor(180, 180, 180);
      // Top border
      pdf.line(
        margin,
        yPosition - rowHeight * (dataRows.length + 1) - 4,
        margin + tableWidth,
        yPosition - rowHeight * (dataRows.length + 1) - 4,
      );
      // Bottom border
      pdf.line(margin, yPosition - 5, margin + tableWidth, yPosition - 5);

      yPosition += 3;
    };

    // Helper function to calculate space needed for a list section
    const calculateListSectionHeight = (
      startIndex: number,
      headerLine: string,
    ): number => {
      let totalHeight = 0;

      // Add header height
      pdf.setFontSize(10);
      const headerLines = pdf.splitTextToSize(headerLine, contentWidth);
      totalHeight += headerLines.length * 6 + 3; // 6mm per line + 3mm spacing

      // Count consecutive list items
      let i = startIndex + 1;
      while (i < lines.length && isListItem(lines[i])) {
        const itemLine = lines[i].trim();
        const itemLines = pdf.splitTextToSize(itemLine, contentWidth);
        totalHeight += itemLines.length * 6;
        i++;
      }

      return totalHeight;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        yPosition += 4;
        continue;
      }

      // Check for table rows - collect all consecutive table rows
      if (isTableRow(line)) {
        const tableLines: string[] = [];
        while (i < lines.length && isTableRow(lines[i])) {
          tableLines.push(lines[i]);
          i++;
        }
        i--; // Adjust for the outer loop increment
        renderTable(tableLines);
        continue;
      }

      // Check if next line is a list item - if so, keep current line with the list
      if (i + 1 < lines.length && isListItem(lines[i + 1])) {
        const sectionHeight = calculateListSectionHeight(i, trimmedLine);
        // Check if entire section fits on current page
        if (yPosition + sectionHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
      }

      // Check for markdown headers (##, ###, etc.)
      const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        checkPageBreak(10);
        const level = headerMatch[1].length;
        const headerText = headerMatch[2].trim();

        yPosition += 3;
        if (level === 1) {
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
        } else if (level === 2) {
          pdf.setFontSize(13);
          pdf.setFont('helvetica', 'bold');
        } else {
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
        }

        const headerLines = pdf.splitTextToSize(headerText, contentWidth);
        headerLines.forEach((headerLine: string) => {
          checkPageBreak(7);
          pdf.text(headerLine, margin, yPosition);
          yPosition += 7;
        });
        yPosition += 2;
        continue;
      }

      // Process regular text with bold rendering
      pdf.setFontSize(10);

      // Check if line has bold text
      const hasBold = /\*\*([^*]+)\*\*/.test(trimmedLine);

      if (hasBold) {
        // For lines with bold, render each part separately
        const parts: Array<{ text: string; isBold: boolean }> = [];
        const boldRegex = /\*\*([^*]+)\*\*/g;
        let lastIndex = 0;
        let match;

        while ((match = boldRegex.exec(trimmedLine)) !== null) {
          if (match.index > lastIndex) {
            parts.push({
              text: trimmedLine.substring(lastIndex, match.index),
              isBold: false,
            });
          }
          parts.push({ text: match[1], isBold: true });
          lastIndex = match.index + match[0].length;
        }

        if (lastIndex < trimmedLine.length) {
          parts.push({
            text: trimmedLine.substring(lastIndex),
            isBold: false,
          });
        }

        // Render parts on the same line where possible
        let xPos = margin;
        for (const part of parts) {
          if (!part.text) continue;

          pdf.setFont('helvetica', part.isBold ? 'bold' : 'normal');

          // Check if we need to wrap
          const words = part.text.split(' ');
          for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const wordWidth = pdf.getTextWidth(word + ' ');

            if (xPos + wordWidth > pageWidth - margin && xPos > margin) {
              // Wrap to next line
              yPosition += 6;
              checkPageBreak(6);
              xPos = margin;
            }

            pdf.text(word + (i < words.length - 1 ? ' ' : ''), xPos, yPosition);
            xPos += wordWidth;
          }
        }
        yPosition += 6;
      } else {
        // No formatting, render as normal text
        pdf.setFont('helvetica', 'normal');
        const displayText = trimmedLine.replace(/\*([^*]+)\*/g, '$1');

        const textLines = pdf.splitTextToSize(displayText, contentWidth);
        textLines.forEach((textLine: string) => {
          checkPageBreak(6);
          pdf.text(textLine, margin, yPosition);
          yPosition += 6;
        });
      }
    }

    // Add Risk Alerts section if present
    const riskAlerts = report.risk_alerts;
    if (riskAlerts && riskAlerts.length > 0) {
      checkPageBreak(20);
      yPosition += 5;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Reference Universe: Risk Alerts', margin, yPosition);
      yPosition += 10;

      riskAlerts.forEach((alert, index) => {
        checkPageBreak(25);

        // Alert title with level badge
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        const alertTitle = cleanText(alert.title);

        // Draw level badge
        const levelColors: Record<string, { bg: number[]; text: number[] }> = {
          high: { bg: [254, 226, 226], text: [185, 28, 28] }, // Red
          medium: { bg: [254, 243, 199], text: [161, 98, 7] }, // Yellow/Amber
          low: { bg: [220, 252, 231], text: [22, 101, 52] }, // Green
        };
        const levelColor = levelColors[alert.level] || levelColors.medium;
        const levelText =
          alert.level.charAt(0).toUpperCase() + alert.level.slice(1);
        const badgeWidth = pdf.getTextWidth(levelText) + 4;

        // Draw badge background
        pdf.setFillColor(levelColor.bg[0], levelColor.bg[1], levelColor.bg[2]);
        pdf.roundedRect(margin, yPosition - 4, badgeWidth + 2, 6, 1, 1, 'F');

        // Draw badge text
        pdf.setFontSize(8);
        pdf.setTextColor(
          levelColor.text[0],
          levelColor.text[1],
          levelColor.text[2],
        );
        pdf.text(levelText, margin + 2, yPosition);

        // Draw urgency badge if present
        let nextBadgeX = margin + badgeWidth + 4;
        if (alert.urgency) {
          const urgencyColors: Record<
            string,
            { bg: number[]; text: number[] }
          > = {
            immediate: { bg: [254, 202, 202], text: [153, 27, 27] }, // Darker red
            monitor: { bg: [219, 234, 254], text: [30, 64, 175] }, // Blue
          };
          const urgencyColor =
            urgencyColors[alert.urgency] || urgencyColors.monitor;
          const urgencyText =
            alert.urgency.charAt(0).toUpperCase() + alert.urgency.slice(1);
          pdf.setFontSize(8);
          const urgencyBadgeWidth = pdf.getTextWidth(urgencyText) + 4;

          pdf.setFillColor(
            urgencyColor.bg[0],
            urgencyColor.bg[1],
            urgencyColor.bg[2],
          );
          pdf.roundedRect(
            nextBadgeX,
            yPosition - 4,
            urgencyBadgeWidth + 2,
            6,
            1,
            1,
            'F',
          );
          pdf.setTextColor(
            urgencyColor.text[0],
            urgencyColor.text[1],
            urgencyColor.text[2],
          );
          pdf.text(urgencyText, nextBadgeX + 2, yPosition);
          nextBadgeX += urgencyBadgeWidth + 4;
        }

        // Reset text color and draw title
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        const titleX = nextBadgeX + 2;
        const availableTitleWidth = contentWidth - (titleX - margin);
        const titleLines = pdf.splitTextToSize(alertTitle, availableTitleWidth);
        titleLines.forEach((line: string, lineIndex: number) => {
          checkPageBreak(7);
          pdf.text(line, lineIndex === 0 ? titleX : margin, yPosition);
          yPosition += 7;
        });

        // Confidence indicator
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        const confidenceText = `Confidence: ${Math.round(alert.confidence * 100)}%`;
        pdf.text(confidenceText, margin, yPosition);
        yPosition += 6;

        // Reset text color for message
        pdf.setTextColor(0, 0, 0);

        // Alert message
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const cleanedMessage = cleanText(alert.message);
        const messageLines = pdf.splitTextToSize(cleanedMessage, contentWidth);
        messageLines.forEach((messageLine: string) => {
          checkPageBreak(6);
          pdf.text(messageLine, margin, yPosition);
          yPosition += 6;
        });

        // Data points table if present
        if (alert.data_points && alert.data_points.length > 0) {
          yPosition += 4;
          checkPageBreak(30);

          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Key Metrics:', margin, yPosition);
          yPosition += 6;

          // Render data points as a mini table
          const dpHeaders = ['Metric', 'Value', 'Threshold', 'Concern'];
          const dpColWidths = [40, 30, 30, contentWidth - 100];
          const dpRowHeight = 6;

          // Header row
          pdf.setFillColor(245, 245, 245);
          pdf.rect(margin, yPosition - 4, contentWidth, dpRowHeight, 'F');
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          let dpX = margin;
          dpHeaders.forEach((header, idx) => {
            pdf.text(header, dpX + 1, yPosition);
            dpX += dpColWidths[idx];
          });
          yPosition += dpRowHeight;

          // Data rows
          pdf.setFont('helvetica', 'normal');
          alert.data_points.forEach((dp) => {
            checkPageBreak(dpRowHeight);
            dpX = margin;
            const values = [dp.metric, dp.value, dp.threshold, dp.concern];
            values.forEach((val, idx) => {
              const cellText = cleanText(val || '-');
              const truncated =
                cellText.length > 25
                  ? cellText.substring(0, 22) + '...'
                  : cellText;
              pdf.text(truncated, dpX + 1, yPosition);
              dpX += dpColWidths[idx];
            });
            pdf.setDrawColor(220, 220, 220);
            pdf.line(
              margin,
              yPosition + 2,
              margin + contentWidth,
              yPosition + 2,
            );
            yPosition += dpRowHeight;
          });
          yPosition += 2;
        }

        // Affected positions if present
        if (alert.affected_positions && alert.affected_positions.length > 0) {
          checkPageBreak(12);
          yPosition += 2;
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Affected Positions:', margin, yPosition);
          yPosition += 5;

          pdf.setFont('helvetica', 'normal');
          const positionsText = alert.affected_positions.join(', ');
          const positionsLines = pdf.splitTextToSize(
            cleanText(positionsText),
            contentWidth,
          );
          positionsLines.forEach((line: string) => {
            checkPageBreak(5);
            pdf.text(line, margin, yPosition);
            yPosition += 5;
          });
        }

        // Extended reasoning if present
        if (alert.extended_reasoning) {
          checkPageBreak(15);
          yPosition += 3;
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Analysis:', margin, yPosition);
          yPosition += 5;

          pdf.setFont('helvetica', 'normal');
          const reasoningLines = pdf.splitTextToSize(
            cleanText(alert.extended_reasoning),
            contentWidth,
          );
          reasoningLines.forEach((line: string) => {
            checkPageBreak(5);
            pdf.text(line, margin, yPosition);
            yPosition += 5;
          });
        }

        // Recommended action if present
        if (alert.recommended_action) {
          checkPageBreak(15);
          yPosition += 3;
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(37, 99, 235); // Blue for action
          pdf.text('Recommended Action:', margin, yPosition);
          yPosition += 5;

          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(0, 0, 0);
          const actionLines = pdf.splitTextToSize(
            cleanText(alert.recommended_action),
            contentWidth,
          );
          actionLines.forEach((line: string) => {
            checkPageBreak(5);
            pdf.text(line, margin, yPosition);
            yPosition += 5;
          });
        }

        // Add spacing between alerts
        if (index < riskAlerts.length - 1) {
          yPosition += 10;
          // Draw a light separator line
          pdf.setDrawColor(200, 200, 200);
          pdf.line(margin, yPosition - 5, margin + contentWidth, yPosition - 5);
        }
      });
    }

    // Add analyst notes if present
    if (report.analyst_notes && report.analyst_notes.trim()) {
      checkPageBreak(20);
      yPosition += 5;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Analyst Notes', margin, yPosition);
      yPosition += 7;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const cleanedNotes = cleanText(report.analyst_notes);
      const notesLines = pdf.splitTextToSize(cleanedNotes, contentWidth);
      notesLines.forEach((noteLine: string) => {
        checkPageBreak(6);
        pdf.text(noteLine, margin, yPosition);
        yPosition += 6;
      });
    }

    // Generate filename: "banqora analysis <portfolio name> date"
    const date = new Date(report.generated_at)
      .toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
      .replace(/\//g, '-');

    const portfolioStr = portfolioName
      ? ` ${portfolioName.replace(/[^a-z0-9\s]/gi, '')}`
      : '';
    const filename = `banqora analysis${portfolioStr} ${date}.pdf`;

    pdf.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};
